"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.Carousel = void 0;
var _testidGenerator = _interopRequireDefault(require("@ori/testid-generator"));
var _clsx = _interopRequireDefault(require("clsx"));
var _react = require("keen-slider/react");
var _lodash = _interopRequireDefault(require("lodash.debounce"));
var _lodash2 = _interopRequireDefault(require("lodash.get"));
var _react2 = require("react");
var _uuid = require("uuid");
var _common = require("../../common");
var _ImagePlaceholder = require("../ImagePlaceholder");
var _NavigationArrows = require("./NavigationArrows");
var _Pagination = require("./Pagination");
var _carouselClasses = require("./carouselClasses");
var _constants = require("./constants");
var _hooks = require("./hooks");
var _plugins = require("./plugins");
var _styles = require("./styles");
var _utils = require("./utils");
var _jsxRuntime = require("react/jsx-runtime");
const Carousel = ({
  autoplay,
  className,
  children,
  id,
  showPlaceholderIfEmpty,
  navigation,
  pagination,
  propagateDragEvent,
  testId,
  slider,
  width,
  hasFocusableSlides = false,
  title,
  titleMaxLines,
  renderPaginationStep
}) => {
  const autoplayProps = (0, _hooks.useAutoplayProps)(autoplay);
  const navigationProps = (0, _hooks.useNavigationProps)(navigation);
  const paginationProps = (0, _hooks.usePaginationProps)(pagination);
  const selectorId = (0, _react2.useMemo)(() => id ?? `x${(0, _uuid.v4)()}`, [id]);
  const sliderProps = (0, _hooks.useSliderProps)({
    selector: `#${selectorId} > .${_constants.SLIDE_CLASSNAME}`,
    ...slider
  });
  const [currentSlide, setCurrentSlide] = (0, _react2.useState)(0);
  const [slidesCount, setSlidesCount] = (0, _react2.useState)(_react2.Children.count(children));
  const [lastSlideVisible, setLastSlideVisible] = (0, _react2.useState)(false);
  const [isTouchActive, setIsTouchActive] = (0, _react2.useState)(false);
  const {
    getTestId
  } = (0, _testidGenerator.default)();
  const shouldRenderTopArrows = navigation?.arrowsMode?.type === 'compact' && navigation.arrowsMode.position.includes('top');
  const shouldRenderBottomArrows = navigation?.arrowsMode?.type === 'compact' && navigation.arrowsMode.position.includes('bottom');
  const shouldRenderHeader = !!title || shouldRenderTopArrows;
  const rootClassName = (0, _clsx.default)(_carouselClasses.carouselClasses.root, className);
  const innerClassName = (0, _clsx.default)(_carouselClasses.carouselClasses.inner, _constants.SLIDER_CLASSNAME);
  const updateLastSlideVisible = (0, _react2.useCallback)(progress => {
    setLastSlideVisible((0, _utils.isCarouselAtTheEnd)(progress));
  }, []);
  const options = (0, _react2.useMemo)(() => {
    const updateLastSlideVisibleDebounce = (0, _lodash.default)(updateLastSlideVisible, _constants.WAIT_TIME);
    return {
      ...sliderProps,
      created: instance => {
        setSlidesCount((0, _lodash2.default)(instance, 'track.details.slides.length', _react2.Children.count(children)));
        (0, _utils.disableDragEventPropagation)({
          instance,
          propagateDragEvent
        });
        sliderProps.created?.(instance);
        // add active class for the first slide
        const firstSlide = instance.slides[0];
        firstSlide?.classList.add(_constants.ACTIVE_SLIDE_CLASSNAME);
      },
      destroyed: instance => {
        (0, _utils.removeDisableDragEventPropagation)({
          instance,
          propagateDragEvent
        });
        sliderProps.destroyed?.(instance);
      },
      slideChanged: instance => {
        const newCurrentSlide = (0, _lodash2.default)(instance, 'track.details.rel', 0);
        setCurrentSlide(newCurrentSlide);
        sliderProps.slideChanged?.(instance);
        // find previous active slide by index and remove active class
        const previousActiveSlide = instance.slides.find(slideItem => slideItem.classList.contains(_constants.ACTIVE_SLIDE_CLASSNAME));
        previousActiveSlide?.classList.remove(_constants.ACTIVE_SLIDE_CLASSNAME);
        // find new active slide by index and add active class
        const slide = instance.slides[newCurrentSlide];
        slide?.classList.add(_constants.ACTIVE_SLIDE_CLASSNAME);
      },
      detailsChanged: instance => {
        updateLastSlideVisibleDebounce((0, _lodash2.default)(instance, 'track.details.progress', 0));
        sliderProps.detailsChanged?.(instance);
      }
    };
  }, [children, propagateDragEvent, sliderProps, updateLastSlideVisible]);
  const [sliderRef, instanceRef] = (0, _react.useKeenSlider)(options, (0, _plugins.usePlugins)({
    autoplay: autoplayProps,
    children,
    className,
    navigation: navigationProps,
    pagination: paginationProps,
    propagateDragEvent,
    slider: sliderProps,
    testId,
    width,
    hasFocusableSlides
  }, setIsTouchActive));
  (0, _react2.useEffect)(() => {
    const childrenCount = _react2.Children.count(children);
    if (instanceRef.current) {
      instanceRef.current.update(options, (0, _utils.getCurrentSlideIndex)({
        currentSlideIndex: (0, _lodash2.default)(instanceRef, 'current.track.details.rel', 0),
        slidesCount: childrenCount
      }));
      setSlidesCount((0, _lodash2.default)(instanceRef, 'current.track.details.slides.length', childrenCount));
    } else {
      setSlidesCount(childrenCount);
    }
  }, [children, instanceRef, options]);
  const navigationArrows = (0, _utils.shouldShowNavigationArrows)({
    navigation: navigationProps,
    slider: sliderProps,
    slidesCount
  }) ? /*#__PURE__*/(0, _jsxRuntime.jsx)(_NavigationArrows.NavigationArrows, {
    instanceRef: instanceRef,
    lastSlideVisible: lastSlideVisible,
    navigation: navigationProps,
    pagination: paginationProps,
    slider: sliderProps,
    slidesCount: slidesCount,
    mode: navigationProps.arrowsMode
  }) : null;
  if (slidesCount < 1) {
    return showPlaceholderIfEmpty ? /*#__PURE__*/(0, _jsxRuntime.jsx)(_styles.CarouselRoot, {
      "data-testid": getTestId(_common.CAROUSEL_WRAPPER),
      width: width,
      children: /*#__PURE__*/(0, _jsxRuntime.jsx)(_ImagePlaceholder.ImagePlaceholder, {
        isSquare: true
      })
    }) : null;
  }
  return /*#__PURE__*/(0, _jsxRuntime.jsxs)(_styles.CarouselRoot, {
    className: rootClassName,
    "data-testid": testId ?? getTestId(_common.CAROUSEL_WRAPPER),
    isTouchActive: isTouchActive,
    navigation: navigationProps,
    width: width,
    hasFocusableSlides: hasFocusableSlides,
    children: [shouldRenderHeader ? /*#__PURE__*/(0, _jsxRuntime.jsxs)(_styles.HeaderContainer, {
      hasTitle: !!title,
      className: _carouselClasses.carouselClasses.header,
      children: [title ? /*#__PURE__*/(0, _jsxRuntime.jsx)(_styles.Title, {
        variant: "h4",
        color: "textPrimary",
        maxLines: titleMaxLines,
        className: _carouselClasses.carouselClasses.title,
        children: title
      }) : null, shouldRenderTopArrows ? navigationArrows : null]
    }) : null, /*#__PURE__*/(0, _jsxRuntime.jsx)(_styles.CarouselInner, {
      ref: sliderRef,
      hasFocusableSlides: hasFocusableSlides,
      id: selectorId,
      className: innerClassName,
      "data-testid": getTestId(_common.CAROUSEL),
      children: children
    }), paginationProps.show ? /*#__PURE__*/(0, _jsxRuntime.jsx)(_Pagination.Pagination, {
      autoplay: autoplayProps,
      slidesCount: slidesCount,
      currentSlide: currentSlide,
      instanceRef: instanceRef,
      pagination: paginationProps,
      renderPaginationStep: renderPaginationStep
    }) : null, shouldRenderBottomArrows ? /*#__PURE__*/(0, _jsxRuntime.jsx)(_styles.CompactButtonsRoot, {
      className: _carouselClasses.carouselClasses.compactButtonsRoot,
      children: navigationArrows
    }) : navigationProps.arrowsMode.type === 'sides' ? navigationArrows : null]
  });
};
exports.Carousel = Carousel;
//# sourceMappingURL=Carousel.js.map